/**
 * NGUI Main Application Component
 * MAIA Voice Interface with Authentication
 *
 * TypeScript-based React application with voice AI interaction.
 * Supports multiple UI modes (default, employee) via UiModeContext.
 */

import React, { Suspense } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

// Import context providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModuleRefreshProvider } from './contexts/ModuleRefreshContext';
import { TenantProvider } from './contexts/TenantContext';
import { DisplayModeProvider } from './contexts/DisplayModeContext';
import { UiModeProvider, useUiMode } from './contexts/UiModeContext';
import { useLanguageSync } from './hooks/useLanguageSync';
import { useSystemStatus } from './hooks/useSystemStatus';

// Import components
import VoiceInterface from './components/VoiceInterface';
import NetworkStatus from './components/NetworkStatus';
import HealthBanner from './components/HealthBanner';

// Import mode registry
import { getModeDefinition } from './modes';

/**
 * App Content Component - Renders persistent VoiceInterface + Mode Router
 */
const AppContent: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { effectiveMode, loading: modeLoading } = useUiMode();
  useLanguageSync();

  // Wait for mode to resolve before rendering anything mode-specific.
  // Without this guard, the race condition is:
  //   1. TenantProvider loading -> UiModeProvider falls back to 'default' (hasVoiceInterface: true)
  //   2. AuthProvider reads token from localStorage -> isAuthenticated=true (SYNC, immediate)
  //   3. VoiceInterface MOUNTS (WebSocket connects, AudioContext created, mic requested)
  //   4. Tenant fetch completes -> mode becomes 'employee' (hasVoiceInterface: false)
  //   5. VoiceInterface UNMOUNTS -> WebSocket close, AudioContext close, avatar stop, media stop
  if (modeLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const modeDef = getModeDefinition(effectiveMode);

  // VoiceInterface only for modes that support it
  const showVoice = modeDef.hasVoiceInterface && isAuthenticated && location.pathname !== '/login';

  // Determine if VoiceInterface should be minimized (floating button)
  const isModuleRoute = ['/calendar', '/knowledge', '/disk'].includes(location.pathname) ||
    location.pathname.startsWith('/tasks') ||
    location.pathname.startsWith('/disk/') ||
    location.pathname.startsWith('/people') ||
    location.pathname.startsWith('/email') ||
    location.pathname.startsWith('/chat') ||
    location.pathname.startsWith('/contractors');
  const isStandalonePage = ['/admin', '/settings', '/billing', '/support'].includes(location.pathname) ||
    location.pathname.startsWith('/admin/');
  const isPreviewPage = location.pathname.includes('/preview');
  const showFloatingVoice = isAuthenticated && (isModuleRoute || isStandalonePage || isPreviewPage);

  const ModeComponent = modeDef.component;

  return (
    <>
      {/* Persistent Voice Interface - Only for modes that support it */}
      {showVoice && (
        <VoiceInterface
          key="persistent-voice"
          minimized={showFloatingVoice}
        />
      )}

      {/* Mode component renders its own Routes */}
      <Suspense fallback={
        <div className="min-h-screen w-full flex items-center justify-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      }>
        <ModeComponent />
      </Suspense>
    </>
  );
};

/**
 * Main Application Component
 */
function App(): JSX.Element {
  const { maintenance, message } = useSystemStatus();

  return (
    <Router>
      <TenantProvider>
        <UiModeProvider>
          <DisplayModeProvider>
            <AuthProvider>
              <ModuleRefreshProvider>
                {/* Maintenance Mode Overlay */}
                {maintenance && (
                  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
                      <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        System Update in Progress
                      </h2>
                      <p className="text-gray-600">
                        {message || "We're updating the system \u2014 this will only take a moment."}
                      </p>
                      <p className="text-sm text-gray-400 mt-4">
                        You'll be back to work automatically when the update is complete.
                      </p>
                    </div>
                  </div>
                )}

                {/* Global Health Banner */}
                <HealthBanner />
                <div
                  className="health-banner-spacer"
                  style={{ height: 'var(--health-banner-height, 0px)' }}
                  aria-hidden="true"
                />

                {/* Global Network Status Monitor */}
                <NetworkStatus />

                <AppContent />
              </ModuleRefreshProvider>
            </AuthProvider>
          </DisplayModeProvider>
        </UiModeProvider>
      </TenantProvider>
    </Router>
  );
}

export default App;
